import { Menu, MenuItem, Stack, Typography } from '@mui/material';
import {
  ArrowLeftRight,
  Clock,
  Handshake,
  LogOut,
  type LucideIcon,
  Monitor,
  Moon,
  Sun,
} from 'lucide-react';
import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { ColoredAvatar, ShortcutText, Tooltip } from 'src/components';
import { OptionSwitchMenu } from 'src/components/Navbar/AppMenu/option-switch-menu';
import { usePartnersDialogOptions } from 'src/components/Navbar/use-partners-dialog-options';
import { useThemeSwitch } from 'src/components/Navbar/use-theme-switch';
import { useTimeFormatSwitch } from 'src/components/Navbar/use-time-format-switch';
import { appShortcuts } from 'src/constants/appShortcuts';
import { useSelectedPartner } from 'src/graphql';
import { useTimeFormat } from 'src/graphql/entities/time-format/use-time-format';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods } from 'src/store/modals';
import { getDropdownMenuPaperSx } from 'src/utils/dropdown-paper';
import { useLogout, useRegisterShortcuts } from 'src/utils/hooks';
import { useScreenDimensions } from 'src/utils/system';
import { ItemDivider, SupportLinks } from './SupportLinks/support-links';

const REFER_PARTNER_LINK = 'https://www.onevisionresources.com/refer-a-partner';

const THEME_ICONS: Record<string, LucideIcon> = {
  light_mode: Sun,
  dark_mode: Moon,
  contrast: Monitor,
};

export const AppMenu = ({
  anchor,
  onClose,
}: {
  anchor: HTMLElement | null;
  onClose: () => void;
}) => {
  const {
    selectedTheme,
    setForceThemeMode,
    themeModalAnchor,
    openThemeSwitch,
    closeThemeSwitch,
    themeOptions,
  } = useThemeSwitch();

  const { isDesktop } = useScreenDimensions();
  const { logout } = useLogout();
  const isMultiTenancyMode = useGlobalStore.use.isMultiTenancyMode();

  const { selectedPartner } = useSelectedPartner();
  const { showModal: showSelectPartnerModal } = useModalMethods(Modals.SelectPartnerModal);
  const { partnersOptions: allPartnersOptions } = usePartnersDialogOptions({
    currentPartner: false,
  });

  const [clockFormatSwitchAnchor, setClockFormatSwitchAnchor] = useState<null | HTMLElement>(
    null,
  );

  const { currentTimeFormat, changeTimeFormat } = useTimeFormat();

  const canSwitchPartner = useMemo(() => {
    return allPartnersOptions.some(
      (option) => option.id !== null && option.id !== selectedPartner?.partnerId,
    );
  }, [allPartnersOptions, selectedPartner?.partnerId]);

  const canOpenPartnerDialog = isMultiTenancyMode && canSwitchPartner;

  useRegisterShortcuts(
    useMemo(
      () =>
        canOpenPartnerDialog
          ? [
              {
                ...appShortcuts.openSelectPartnerDialog,
                callback: () => {
                  showSelectPartnerModal(null);
                  onClose();
                  return false;
                },
              },
            ]
          : [],
      [canOpenPartnerDialog, onClose, showSelectPartnerModal],
    ),
  );
  const { selectedTimeFormatLabel, timeFormatsMenu } = useTimeFormatSwitch({
    isOpen: Boolean(clockFormatSwitchAnchor),
    anchorEl: clockFormatSwitchAnchor,
    selectedFormat: currentTimeFormat,
    onClose: () => setClockFormatSwitchAnchor(null),
    onChange: (value) => {
      changeTimeFormat(value);
      onClose();
    },
  });

  const switchPartnerMenuItem = selectedPartner ? (
    <Stack
      direction="row"
      sx={{
        gap: '1rem',
        width: '16rem',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <ColoredAvatar name={selectedPartner.businessName} />

      <Tooltip
        title={
          <ShortcutText
            label="Switch Partner"
            shortcut={appShortcuts.openSelectPartnerDialog.command}
            size="small"
          />
        }
      >
        <Typography flex={1} textAlign="left" overflow="hidden" textOverflow="ellipsis">
          {selectedPartner.businessName}
        </Typography>
      </Tooltip>
      <ArrowLeftRight size={20} />
    </Stack>
  ) : (
    <Stack
      direction="row"
      sx={{
        gap: '1rem',
        width: '8rem',
      }}
    >
      <ArrowLeftRight size={20} />

      <Tooltip
        title={
          <ShortcutText
            label="Switch Partner"
            shortcut={appShortcuts.openSelectPartnerDialog.command}
            size="small"
          />
        }
      >
        <Typography textAlign="center">Switch Partner</Typography>
      </Tooltip>
    </Stack>
  );

  const openClockFormatSwitch = (event: MouseEvent<HTMLElement>) => {
    setClockFormatSwitchAnchor((current) => (current ? null : event.currentTarget));
  };

  const openReferPartnerLink = useCallback(() => {
    window.open(REFER_PARTNER_LINK, '_blank');
    onClose();
  }, [onClose]);

  const ThemeIcon = THEME_ICONS[selectedTheme?.icon ?? ''] ?? Monitor;

  return (
    <>
      <Menu
        anchorEl={anchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isDesktop ? 'left' : 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: isDesktop ? 'left' : 'right',
        }}
        open={Boolean(anchor)}
        onClose={onClose}
        slotProps={{
          paper: {
            sx: (theme) => getDropdownMenuPaperSx(theme),
          },
        }}
      >
        <MenuItem
          data-testid="app-menu-switch-partner"
          disabled={!canOpenPartnerDialog}
          onClick={() => {
            if (!canOpenPartnerDialog) return;
            showSelectPartnerModal(null);
            onClose();
          }}
        >
          {switchPartnerMenuItem}
        </MenuItem>

        {selectedPartner?.currentPartner === 'yes' && (
          <SupportLinks anchor={anchor} onCloseMenu={onClose} />
        )}

        <MenuItem sx={{ gap: '1rem' }} onClick={openThemeSwitch}>
          <ThemeIcon size={20} />
          <Typography textAlign="center">{selectedTheme?.label} Theme</Typography>
        </MenuItem>

        <ItemDivider />

        <MenuItem sx={{ gap: '1rem' }} onClick={openClockFormatSwitch}>
          <Clock size={20} />
          <Typography textAlign="center">{selectedTimeFormatLabel}</Typography>
        </MenuItem>

        <ItemDivider />

        <MenuItem sx={{ gap: '1rem' }} onClick={openReferPartnerLink}>
          <Handshake size={20} />
          <Typography textAlign="center">Refer a Partner ($)</Typography>
        </MenuItem>

        <MenuItem sx={{ gap: '1rem' }} onClick={logout}>
          <LogOut size={20} />
          <Typography textAlign="center">Log out</Typography>
        </MenuItem>
      </Menu>

      {timeFormatsMenu}

      <OptionSwitchMenu
        anchorEl={themeModalAnchor}
        onClose={closeThemeSwitch}
        selectedValue={selectedTheme?.value}
        options={themeOptions.map((option) => ({
          value: option.value,
          label: option.label,
          icon: THEME_ICONS[option.icon] ?? Monitor,
        }))}
        onSelect={(value) => {
          setForceThemeMode(value);
          closeThemeSwitch();
          onClose();
        }}
      />
    </>
  );
};
